import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { MenuItem, MessageService } from 'primeng/api';


export enum AlertType {
    Success = 'Success',
    Error = 'Error',
    Info = 'Info',
    Warning = 'Warning'
}

export class Alert {
    id: string = '';
    type: AlertType = AlertType.Info;
    message: string = '';
    autoClose: boolean = false;
    keepAfterRouteChange: boolean = false;
    fade: boolean = false;
    life?: number; // Add this line

    constructor(init?: Partial<Alert>) {
        Object.assign(this, init);
    }
}


@Injectable({ providedIn: 'root' })
export class AlertService {
    private subject = new Subject<Alert>();
    private defaultId = 'default-alert';
    private activeAlerts: Alert[] = [];

    constructor(
        private messages: MessageService) { }

    // Enable subscribing to alerts observable
    onAlert(id = this.defaultId): Observable<Alert> {
        return this.subject.asObservable().pipe(filter(x => x && x.id === id));
    }

    // Convenience methods with enhanced options
    success(message: string, options?: Partial<Alert>) {
        console.debug('AlertService: success called with message:', message, 'and options:', options);
        // this.alert(new Alert({
        //     ...options,
        //     autoClose: options?.autoClose ?? true,
        //     type: AlertType.Success,
        //     message
        // }));
        this.messages.add({
            key: 'global',
            severity: 'success',
            summary: 'Success',
            detail: message,
            life: 6000
        });

    }

    error(message: string, options?: Partial<Alert>) {
        this.alert(new Alert({
            ...options,
            autoClose: options?.autoClose ?? false, // Errors should be sticky by default
            type: AlertType.Error,
            message
        }));
    }

    info(message: string, options?: Partial<Alert>) {
        this.alert(new Alert({
            ...options,
            autoClose: options?.autoClose ?? true,
            type: AlertType.Info,
            message
        }));
    }

    warn(message: string, options?: Partial<Alert>) {
        this.alert(new Alert({
            ...options,
            autoClose: options?.autoClose ?? false, // Warnings should be sticky by default
            type: AlertType.Warning,
            message
        }));
    }

    // Enhanced convenience methods for better UX
    successWithAction(message: string, actionLabel: string, actionCallback: () => void, options?: Partial<Alert>) {
        this.alert(new Alert({
            ...options,
            autoClose: false, // Allow user to see the action
            type: AlertType.Success,
            message: `${message} <button class="btn btn-sm btn-outline-success ms-2" onclick="(${actionCallback.toString()})()">${actionLabel}</button>`
        }));
    }

    errorWithRetry(message: string, retryCallback: () => void, options?: Partial<Alert>) {
        this.alert(new Alert({
            ...options,
            autoClose: false,
            type: AlertType.Error,
            message: `${message} <button class="btn btn-sm btn-outline-danger ms-2" onclick="(${retryCallback.toString()})()" >Retry</button>`
        }));
    }

    // Main alert method    
    alert(alert: Alert) {
        console.info('AlertService: alert called with:', alert);
        if (!alert.id) {
            alert.id = this.defaultId;
        }
        this.activeAlerts.push(alert);
        this.subject.next(alert);
    }

    getActiveAlertsForId(id: string): Alert[] {
        return this.activeAlerts.filter(alert => alert.id === id);
    }

    // Clear alerts
    clear(id = this.defaultId) {
        this.activeAlerts = this.activeAlerts.filter(alert => alert.id !== id);
        this.subject.next(new Alert({ id }));
    }

    // Clear all alerts across all instances
    clearAll() {
        this.activeAlerts = [];
        this.subject.next(new Alert({ id: '*' }));
    }
}
